import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useImageAccentColor from '@/hooks/useImageAccentColor';
import ViewsIcon from '@/shared-components/icons/components/user-profile/ViewsIcon';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';
import { formatArticleReadingTime } from '@/utils/articleReadingTime';
import { buildPublicContentPath, resolvePublicContentKind } from '@/utils/contentRoutes';

import './BoardCard.css';

export default function BoardCard({
  data,
  style = {},
  infiniteScrollObserver,
  ignoreViews = false,
  variant = 'default',
}) {
  const navigate = useNavigate();
  const imageWrapperRef = useRef(null);
  const coverImageId = data?.post_container?.post_cover?.image_id;
  const coverImageUrl = coverImageId
    ? `${config.app.imageServiceDomain}/${coverImageId}/w=300`
    : null;
  const accentColor = useImageAccentColor(coverImageUrl);

  // If data not ready, render nothing
  if (!data || !data.post_container) return null;

  const isEditorialVariant = variant === 'editorial';
  const isBoardVariant = variant === 'board';

  // Determine if this is an article vs a plain post
  const contentKind = resolvePublicContentKind(data);
  const isArticle = contentKind === 'article';
  const readingTimeLabel = isArticle ? formatArticleReadingTime(data) : null;

  // Helper to snapshot scroll and navigate
  function handleNavigate(extraState = {}) {
    navigate(
      buildPublicContentPath(data, contentKind),
      {
        state: {
          from: true,
          ...extraState
        }
      }
    );
  }

  function openCard() {
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.viewTransitionName = `post_${data._id}_animation_cover`;
    }
    handleNavigate();
  }

  return (
    <div
      className={`post-card__container ${isEditorialVariant ? 'post-card__container--editorial' : ''} ${isBoardVariant ? 'post-card__container--board' : ''}`}
      style={{
        ...style,
        '--post-card-hover-bg': accentColor.hoverBackground,
        '--post-card-hover-outline': accentColor.hoverOutline,
      }}
      ref={infiniteScrollObserver}
      role="link"
      tabIndex={0}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        openCard();
      }}
      onKeyDown={e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        openCard();
      }}
    >
      {coverImageId ? (
        <img
          className="post-card__cover"
          src={coverImageUrl}
          alt="Post cover"
          ref={imageWrapperRef}
        />
      ) : (
        <div
          className="post-card__cover post-card__cover--placeholder"
          ref={imageWrapperRef}
        />
      )}
      <div className={`post-card__content ${isEditorialVariant ? 'post-card__content--editorial' : ''} ${isBoardVariant ? 'post-card__content--board' : ''}`}>
        <div className="post-card__header">
          <p className="post-card__post-heading">
            {data.post_container.post_title}
          </p>
          {!ignoreViews && (
            <Text className="post-card__views font-size-medium" paragraph>
              {Utils.numberParserMillionThousand(data.post_visits)}{' '}
              <ViewsIcon className="post-card__views-icon" />
            </Text>
          )}
        </div>

        {data.post_container.post_description && (
          <div className="post-card__excerpt post__text-block">
            {data.post_container.post_description}
          </div>
        )}

        {readingTimeLabel && (
          <div className={`post-card__footer ${isEditorialVariant ? 'post-card__footer--editorial' : ''} ${isBoardVariant ? 'post-card__footer--board' : ''}`}>
            <Text
              className={`post-card__meta ${isEditorialVariant ? 'post-card__meta--editorial' : ''}`}
            >
              {readingTimeLabel}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
